import type React from 'react';
import { useEffect, useRef } from 'react';

/**
 * Хук для отслеживания видимости элемента на экране.
 *
 * Когда элемент становится видимым во время прокрутки, вызывается переданная функция `onVisible`.
 * Возвращает `ref`, который должен быть прикреплен к элементу, видимость которого необходимо отслеживать.
 */
export const useVisibilityChecker = (onVisible: () => void): React.RefObject<HTMLDivElement> => {
    const elementRef = useRef<HTMLDivElement>(null);
    const hasBeenVisibleRef = useRef<boolean>(false);

    useEffect(() => {
        if (!elementRef.current) return;

        const observer = new IntersectionObserver(
            entries => {
                const [entry] = entries;

                if (entry.isIntersecting && !hasBeenVisibleRef.current) {
                    hasBeenVisibleRef.current = true;
                    onVisible();
                }
            },
            {
                root: null,
                threshold: 0.1,
            }
        );

        observer.observe(elementRef.current);

        return () => {
            if (elementRef.current) {
                observer.unobserve(elementRef.current);
            }
        };
    }, [onVisible]);

    return elementRef;
};
